import { useMemo } from 'react';
import {
  NAME_SHEET_FUND_KEY,
  NAME_SHEET_RELATED_ENTITY_KEY,
  RELATED_ENTITY_OWNER_VALUE,
} from 'pages/Waterfall/constants';
import { UseFundInvestmentSheetConfigProps } from './useFundInvestmentSheetConfig.props';
import { FundInvestmentColumn, FundShare } from './useFundInvestmentSheetConfig.types';
import {
  SpreadsheetConfig,
  SpreadsheetConfigConstructorParams,
} from '../../../../../components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import fundInvestmentParser from '../FundInvestmentParser/fundInvestmentParser';
import fundInvestmentRowConfig from '../FundInvestmentRowConfig/fundInvestmentRowConfig';

const useFundInvestmentSheetConfig = ({
  weightedShareValues,
  validSecurities,
  idToAssociateTo,
}: UseFundInvestmentSheetConfigProps) => {
  const fundShares = useMemo<FundShare[]>(() => {
    if (!weightedShareValues || !validSecurities) {
      return [];
    }
    // Always emit one row per valid security so fund sheets always stay aligned
    return weightedShareValues.map(({ fund, share_values, owner }) => ({
      fund,
      owner,
      share_values: validSecurities.map(sec => {
        const existing = share_values.find(sv => sv.securityId === sec.id);
        return {
          securityId: sec.id,
          security: sec.name,
          per_share: existing?.per_share ?? 0,
          shares: existing?.shares ?? 0,
        };
      }),
    }));
  }, [weightedShareValues, validSecurities]);

  const fundSharesConfigs = useMemo(() => {
    if (!fundShares || fundShares.length === 0) {
      return [];
    }
    return fundShares.map(fundShare => {
      const { fund, owner } = fundShare;
      const endSuffix = owner === RELATED_ENTITY_OWNER_VALUE ? NAME_SHEET_RELATED_ENTITY_KEY : NAME_SHEET_FUND_KEY;
      const sheetNameSuffix = idToAssociateTo
        ? `${fund.id}_${idToAssociateTo}_${endSuffix}`
        : `${fund.id}_${endSuffix}`;
      type FundInvestmentConfigParams = SpreadsheetConfigConstructorParams<
        object,
        FundInvestmentColumn,
        object,
        object
      >;
      const configParams = {
        columns: [{ label: 'Share Count' }, { label: 'Value' }],
        rowConfig: fundInvestmentRowConfig({ fundShare }),
        parser: fundInvestmentParser,
        totalParser: () => ({}),
        tableData: {},
        currencyFormatter: () => '',
        unitsFormatter: () => '',
        showTitlesColumn: false,
        name: `FundInvestment_${sheetNameSuffix}`,
      } as unknown as FundInvestmentConfigParams;

      return new SpreadsheetConfig<object, FundInvestmentColumn, object>(configParams);
    });
  }, [fundShares, idToAssociateTo]);

  return { spreadsheets: fundSharesConfigs };
};

export default useFundInvestmentSheetConfig;
